/**
 * ArAgingCenter — the adaptive company-home center for BILLING entities
 * (consultants, attorneys, CPAs…): A/R aging + collections pressure.
 * Mirror of ExpectedRevenueCard (which serves paid-for-work entities).
 *
 * Reuses api.getAgingReport() (dollars, not cents). Shows the AGGREGATE
 * (open A/R + bucket bar + counts) + one CTA to /invoices/aging — it does NOT
 * re-emit per-invoice "send collection notice" cards (the command-center feed
 * already ranks those, per the 2026-06-03 reconciliation audit).
 */
import { Link } from 'react-router-dom';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { GlossaryText } from '@/components/ui/Glossary';
import { Skeleton } from '@/components/ui/Skeleton';
import { useArAging } from '@/hooks/useArAging';
import type { AgingReport } from '@/types';

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
  }).format(n);
}

const BUCKETS = [
  { key: 'current', label: 'Current', cls: 'bg-emerald-500' },
  { key: 'days30', label: '1-30', cls: 'bg-amber-400' },
  { key: 'days60', label: '31-60', cls: 'bg-orange-500' },
  { key: 'days90', label: '61-90', cls: 'bg-rose-400' },
  { key: 'over90', label: '90+', cls: 'bg-rose-600' },
] as const;

/**
 * Pass `data`/`loading` to reuse the home's shared aging fetch (avoids a
 * second call); omit them to let the card fetch standalone.
 */
export function ArAgingCenter({ data: dataProp, loading: loadingProp }: { data?: AgingReport | null; loading?: boolean } = {}) {
  const standalone = dataProp === undefined;
  const own = useArAging(standalone);
  const data = standalone ? own.data : dataProp;
  const loading = standalone ? own.loading : (loadingProp ?? false);
  const error = standalone ? own.error : false;

  if (error) return null; // non-fatal: never block the home
  if (loading) {
    return (
      <Card padding="md">
        <Skeleton className="h-5 w-40 mb-3" />
        <Skeleton className="h-6 w-full rounded-full" />
      </Card>
    );
  }
  if (!data || !data.totalOutstanding || data.invoiceCount === 0) return null;

  const total = data.totalOutstanding;
  return (
    <Card padding="md" data-testid="ar-aging-center">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h2 className="font-headline text-base sm:text-lg font-semibold text-slate-900 dark:text-white">
            <GlossaryText inlineFirstUse={false} text="A/R aging · collections" />
          </h2>
        </div>
        <Link
          to="/invoices/aging"
          className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
        >
          View aging <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <p className="font-mono text-2xl font-semibold text-slate-900 dark:text-white">{fmtUsd(total)}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        open across {data.invoiceCount} invoice{data.invoiceCount === 1 ? '' : 's'}
      </p>

      {/* Stacked bucket bar */}
      <div className="flex h-5 w-full rounded-full overflow-hidden border border-slate-200 dark:border-slate-700">
        {BUCKETS.map((b) => {
          const amt = data.totals?.[b.key] ?? 0;
          const pct = total > 0 ? (amt / total) * 100 : 0;
          if (pct <= 0) return null;
          return <div key={b.key} className={b.cls} style={{ width: `${pct}%` }} title={`${b.label}: ${fmtUsd(amt)}`} />;
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
        {BUCKETS.map((b) => {
          const amt = data.totals?.[b.key] ?? 0;
          if (amt <= 0) return null;
          return (
            <span key={b.key} className="inline-flex items-center gap-1.5">
              <i className={`inline-block w-2.5 h-2.5 rounded-sm ${b.cls}`} />
              {b.label} {fmtUsd(amt)}
            </span>
          );
        })}
      </div>
    </Card>
  );
}
